// Resilience content + helpers. A loss is reframed as a lesson: a clear breakdown
// of what happened, each mentor's in-character reaction, and a concrete recovery
// plan drawn from the game's own mechanics. Never a 'Game Over'.

import type { MentorId, Txn } from '@biz-kid/engine';

/** One in-character line per mentor for a setback. */
export const RESILIENCE_LINES: Record<MentorId, string> = {
  richDad: 'I’ve lost money more times than I can count. Losing isn’t failing — quitting is. So tell me: what did this week teach you?',
  mindsetCoach: 'Every adversity carries the seed of an equal or greater benefit. The setback isn’t the story — what you do next is. Where’s the seed here?',
  investor: 'Even the greatest investors have down years. Zoom out. One red week doesn’t change the trend you’re building.',
  propertyQueen: 'Every landlord has a rough month. You patch the roof, the rent still comes, and you carry on. Steady now.',
  techFounder: 'Every failed launch is just data. Find the bug, ship the fix, and go again — smaller and faster this time.',
  socialStrategist: 'A flop only tells you what didn’t land. Take the note, adjust, and show up again tomorrow.',
  banker: 'A loss is a lesson in your own numbers. Find the leaks, tighten them, keep your credit clean. You’ll be fine.',
  philanthropist: 'The hard weeks build the empathy that makes you a leader people want to follow. Don’t waste the lesson — keep going.',
};

export type CostRow = { label: string; amount: number };
export type LossBreakdown = { moneyIn: number; out: CostRow[]; net: number; why: string };

const COST_LABELS: Partial<Record<Txn['type'], string>> = {
  cogs: 'Ingredients (cost of goods)',
  fixed: 'Fixed costs (rent)',
  marketing: 'Marketing',
  wages: 'Wages',
  living: 'Living costs',
  investor_payout: 'Investor’s share',
  loan_payment: 'Loan repayment',
  liability_upkeep: 'Liability upkeep',
  decision: 'A past decision',
};

/** Build the "what happened" breakdown for a settled week from the ledger. */
export function lossBreakdown(ledger: Txn[], week: number): LossBreakdown {
  const rows = ledger.filter((t) => t.week === week);
  const moneyIn = rows
    .filter((t) => t.category === 'active' || t.category === 'passive')
    .reduce((s, t) => s + Math.max(0, t.amount), 0);
  const outMap = new Map<string, number>();
  for (const t of rows) {
    if (t.amount < 0 && (t.category === 'expense' || t.category === 'debt')) {
      const label = COST_LABELS[t.type] ?? 'Other costs';
      outMap.set(label, (outMap.get(label) ?? 0) + Math.abs(t.amount));
    }
  }
  const out = [...outMap.entries()].map(([label, amount]) => ({ label, amount })).sort((a, b) => b.amount - a.amount);
  const totalOut = out.reduce((s, r) => s + r.amount, 0);
  const net = Math.round(moneyIn - totalOut);
  const biggest = out[0];
  const why = biggest
    ? `Your costs (B$${Math.round(totalOut)}) were bigger than your sales (B$${Math.round(moneyIn)}). The biggest chunk was ${biggest.label.toLowerCase()}. That’s what makes a loss — and it happens to every business.`
    : 'You spent more than you earned this week. That’s a loss — and every business has them.';
  return { moneyIn: Math.round(moneyIn), out, net, why };
}

export type RecoveryAction = { icon: string; text: string };

/** Three concrete next steps, drawn from the game's mechanics, tuned to the loss. */
export function recoveryPlan(input: {
  happiness: number;
  marketing: number;
  loanPayment: number;
  investorPayout: number;
  idleCash: number;
}): RecoveryAction[] {
  const pool: { when: boolean; action: RecoveryAction }[] = [
    { when: input.happiness < 45, action: { icon: '☕', text: 'Take a break to recharge, then push hard — a worn-out owner sells less.' } },
    { when: input.marketing > 20, action: { icon: '📉', text: 'Trim your marketing spend — only pay for ads that bring back more than they cost.' } },
    { when: input.loanPayment > 0, action: { icon: '🏦', text: 'Ease off debt — those weekly loan repayments are eating your profit.' } },
    { when: input.investorPayout > 0, action: { icon: '📊', text: 'Your investor takes a cut of every sale — push volume to out-earn their share.' } },
    { when: true, action: { icon: '🎯', text: 'Find your pricing sweet spot on the trading floor: too high loses customers, too low loses margin.' } },
    { when: input.idleCash >= 50, action: { icon: '🌱', text: 'Put your idle cash into an income asset so it works for you.' } },
    { when: true, action: { icon: '☀️', text: 'Tomorrow, do your morning ritual and choose the growth mindset for a demand edge.' } },
    { when: true, action: { icon: '💪', text: 'Show up again. The only real loss is the one where you quit.' } },
  ];
  return pool
    .filter((p) => p.when)
    .map((p) => p.action)
    .slice(0, 3);
}
